// src/pages/OutOfOfficePage.tsx — Out Of Office (admin / apps.outOfOffice only).
//
// Set the automatic reply on managed shared mailboxes. The tab strip is
// data-driven from /api/ooo/mailboxes (dbo.hub_ooo_mailboxes), so adding
// bookings@ or finance@ later needs no frontend change at all.
//
// On/off only — no scheduling, by design. Both the internal (colleagues) and
// external (customers) messages are editable, because Outlook keeps them
// separate and info@ genuinely uses different text for each.
//
// Save is behind a confirm step: the external message goes out on the railway's
// main public address, so an accidental save is a customer-facing mistake. The
// server also snapshots the previous text into hub_ooo_history for a revert.
//
// Messages are stored by Exchange as HTML. We edit them as plain text and wrap
// on save, stripping tags on load, which keeps the editor honest for the kind of
// short closure notice this app exists for. Anything richer belongs in Outlook.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Field, PermissionLoading, Pill, TopBar } from "../components/ui";

interface MailboxTab { key: string; label: string; address: string; note: string | null }
interface MailboxesResponse { mailboxes: MailboxTab[] }

interface LastChange { at: string; by: string; status: string }
interface AutoReplyResponse {
  key: string;
  label: string;
  address: string;
  note: string | null;
  enabled: boolean;
  status: "disabled" | "alwaysEnabled" | "scheduled";
  internalMessage: string;
  externalMessage: string;
  externalAudience: string;
  lastChange: LastChange | null;
}

/**
 * Exchange stores these as HTML; show the text for editing.
 *
 * The trailing \s* on the <br> and </p> patterns matters more than it looks.
 * Exchange writes `text<br>\ntext` — a tag AND a real newline. Without
 * consuming that newline, one <br> became TWO newlines, and textToHtml then
 * wrote each of them back as its own <br>. The result was a message that gained
 * a blank line between every paragraph on every single save, compounding each
 * time. Verified against the live info@ reply: the round-trip is now stable, so
 * re-saving an untouched message is a no-op.
 */
function htmlToText(html: string): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>\s*/gi, "\n")
    .replace(/<\/p>\s*/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Wrap plain text back into the minimal HTML Exchange expects. */
function textToHtml(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  if (!escaped.trim()) return "";
  return `<html>\n<body>\n${escaped.replace(/\n/g, "<br>\n")}\n</body>\n</html>`;
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function OutOfOfficePage() {
  const navigate = useNavigate();
  const { me, loading: meLoading } = useCurrentUser();

  const [tabs, setTabs] = useState<MailboxTab[] | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [data, setData] = useState<AutoReplyResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notAuthorised, setNotAuthorised] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [internal, setInternal] = useState("");
  const [external, setExternal] = useState("");
  const [dirty, setDirty] = useState(false);

  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const allowed = Boolean(me && (me.isAdmin || me.apps?.outOfOffice));

  useEffect(() => {
    if (!allowed) return;
    apiFetch<MailboxesResponse>("/api/ooo/mailboxes")
      .then((d) => {
        setTabs(d.mailboxes ?? []);
        setActiveKey((prev) => prev ?? d.mailboxes?.[0]?.key ?? null);
      })
      .catch((e) => setLoadError(e instanceof ApiError ? e.message : "Couldn't load the mailbox list."));
  }, [allowed]);

  const load = useCallback((key: string) => {
    setData(null);
    setLoadError(null);
    setNotAuthorised(null);
    setSaved(null);
    setDirty(false);
    apiFetch<AutoReplyResponse>(`/api/ooo/mailboxes/${encodeURIComponent(key)}`)
      .then((d) => {
        setData(d);
        setEnabled(d.enabled);
        setInternal(htmlToText(d.internalMessage));
        setExternal(htmlToText(d.externalMessage));
      })
      .catch((e) => {
        if (e instanceof ApiError && e.body && typeof e.body === "object"
            && (e.body as { error?: string }).error === "GRAPH_FORBIDDEN") {
          setNotAuthorised((e.body as { message?: string }).message ?? e.message);
          return;
        }
        setLoadError(e instanceof ApiError ? e.message : "Couldn't load the auto-reply.");
      });
  }, []);

  useEffect(() => { if (activeKey) load(activeKey); }, [activeKey, load]);

  const save = useCallback(async () => {
    if (!activeKey) return;
    setSaving(true);
    try {
      await apiFetch(`/api/ooo/mailboxes/${encodeURIComponent(activeKey)}`, {
        method: "PUT",
        body: JSON.stringify({
          enabled,
          internalMessage: textToHtml(internal),
          externalMessage: textToHtml(external),
        }),
      });
      setConfirming(false);
      setSaved(enabled ? "Auto-reply is on." : "Auto-reply is off.");
      load(activeKey);
    } catch (e) {
      setConfirming(false);
      setLoadError(e instanceof ApiError ? e.message : "Couldn't save the auto-reply.");
    } finally {
      setSaving(false);
    }
  }, [activeKey, enabled, internal, external, load]);

  if (meLoading) return <PermissionLoading title="Out Of Office" onBack={() => navigate("/apps")} />;

  if (!allowed) {
    return (
      <div>
        <TopBar title="Out Of Office" onBack={() => navigate("/apps")} />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 6 }}>
              You don't have access to Out Of Office.
            </div>
            <div style={{ fontFamily: F.body, fontSize: 14, color: C.inkSoft }}>
              Ask an administrator to grant it in User Access &rarr; Apps.
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar title="Out Of Office" onBack={() => navigate("/apps")} />
      <div style={{ padding: "18px 16px", maxWidth: 760, margin: "0 auto" }}>

        {/* Tab strip — driven by the managed-mailbox register, not hardcoded. */}
        {tabs && tabs.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {tabs.map((t) => {
              const on = t.key === activeKey;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveKey(t.key)}
                  style={{
                    padding: "9px 18px",
                    borderRadius: 999,
                    border: `1px solid ${on ? C.brassDk : C.hairline}`,
                    background: on ? C.brassDk : C.card,
                    color: on ? "#fff" : C.ink,
                    fontFamily: F.body,
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {notAuthorised && (
          <Card style={{ marginBottom: 16, borderColor: C.brassDk }}>
            <Eyebrow>Not authorised yet</Eyebrow>
            <div style={{ fontFamily: F.body, fontSize: 14, color: C.ink, lineHeight: 1.55 }}>
              {notAuthorised}
            </div>
          </Card>
        )}

        {loadError && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: F.body, fontSize: 14, color: C.ink }}>{loadError}</div>
          </Card>
        )}

        {saved && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.ink }}>{saved}</div>
          </Card>
        )}

        {data && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <Eyebrow>{data.address}</Eyebrow>
              {data.note && (
                <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, marginBottom: 12, lineHeight: 1.5 }}>
                  {data.note}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <Pill tone={data.enabled ? "sage" : "paper"}>
                  {data.enabled ? "Auto-reply ON" : "Auto-reply OFF"}
                </Pill>
                {data.status === "scheduled" && (
                  <Pill tone="brass">Scheduled in Outlook</Pill>
                )}
              </div>

              {data.status === "scheduled" && (
                <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, lineHeight: 1.5, marginTop: 8 }}>
                  This mailbox currently uses a scheduled reply set in Outlook. Saving here
                  switches it to a plain on or off.
                </div>
              )}

              {data.lastChange && (
                <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft, marginTop: 10 }}>
                  Last changed here {when(data.lastChange.at)} by {data.lastChange.by}
                </div>
              )}
            </Card>

            <Card style={{ marginBottom: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 4 }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <span style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.ink }}>
                  Send an automatic reply
                </span>
              </label>
              <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, lineHeight: 1.5 }}>
                While this is on, every incoming message gets a reply until you turn it off.
              </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
              <Field
                label="Reply to colleagues (inside NYMR)"
                value={internal}
                onChange={(v) => { setInternal(v); setDirty(true); }}
                rows={5}
                max={20000}
                ph="Thank you for your email, we will respond as soon as possible."
              />
              <Field
                label="Reply to customers and anyone outside NYMR"
                value={external}
                onChange={(v) => { setExternal(v); setDirty(true); }}
                rows={10}
                max={20000}
                ph="Thank you for contacting the North Yorkshire Moors Railway..."
              />
              <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
                The second message is public — it goes to customers who email {data.address}.
              </div>
            </Card>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Button onClick={() => setConfirming(true)} disabled={!dirty || saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
              {dirty ? (
                <Button variant="ghost" onClick={() => activeKey && load(activeKey)} disabled={saving}>
                  Discard changes
                </Button>
              ) : (
                // Without this, a greyed-out Save with no explanation reads as a
                // broken app rather than "nothing to save yet".
                <span style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft }}>
                  Change the switch or a message to enable Save.
                </span>
              )}
            </div>
          </>
        )}

        {!data && !loadError && !notAuthorised && (
          <div style={{ fontFamily: F.body, fontSize: 14, color: C.inkSoft, padding: "8px 2px" }}>
            Loading...
          </div>
        )}
      </div>

      {/* Confirm on save — the external reply is customer-facing. */}
      {confirming && data && (
        <div
          onClick={() => !saving && setConfirming(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(20,18,15,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, zIndex: 90,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.card, borderRadius: 16, padding: 22,
              maxWidth: 460, width: "100%", border: `1px solid ${C.hairline}`,
            }}
          >
            <div style={{ fontFamily: F.serif, fontSize: 19, fontWeight: 700, color: C.ink, marginBottom: 10 }}>
              {enabled ? "Switch the auto-reply on?" : "Switch the auto-reply off?"}
            </div>
            <div style={{ fontFamily: F.body, fontSize: 14, color: C.inkSoft, lineHeight: 1.6, marginBottom: 16 }}>
              {enabled ? (
                <>
                  Everyone who emails <strong style={{ color: C.ink }}>{data.address}</strong> will get
                  an automatic reply, including customers, until you turn it off.
                </>
              ) : (
                <>
                  Messages to <strong style={{ color: C.ink }}>{data.address}</strong> will stop
                  receiving an automatic reply.
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button onClick={save} disabled={saving}>
                {saving ? "Saving..." : enabled ? "Yes, turn it on" : "Yes, turn it off"}
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
